import {deepCopy} from "../../utils/tools";
import {proRataFillPortfolio} from "../../lib/weightSchemes";

/**
 * Get the consolidated stock list and detailed rows from given list of ETFs
 * @param etfs - Object containin etf ticker symbols as keys mapping to documents that MUST include fields
 *        - wgt: weight of the ETF
 *        - holdings: list of stock holdings with fields
 *        {
 *            spy: {
 *                wgt: 0.5,
 *                holdings: [
 *                    {symbol: 'aapl', wgt: 0.123, sector: <>, ...},
 *                    {...}
 *                ]
 *            }
 *        }
 * @returns {{simplifiedObjects: {symbol: *, cumsum: *, wgt: *, index, sector: *, skew, parents: *}[], detailedRows: string[][]}}
 */
function getConsolidatedStockList(etfs) {
    let consolidatedStocks = {};
    let etfWeights = {};
    let etfContributions = {};
    let etfCumulativeWeights = {};

    // Initialize ETF cumulative weights tracker
    for (const etfSymbol of Object.keys(etfs)) {
        etfCumulativeWeights[etfSymbol] = { absolute: 0, relative: 0 };
    }

    // First, calculate total weights for normalization and initialize ETF contributions
    for (const [etfSymbol, etf] of Object.entries(etfs)) {
        etfWeights[etfSymbol] = etf.wgt;
        etfContributions[etfSymbol] = etf.holdings.reduce((acc, curr) => acc + curr.wgt, 0);
    }

    // Consolidate stock weights across ETFs and calculate absolute and relative contributions
    for (const [etfSymbol, etf] of Object.entries(etfs)) {
        const etfWeight = etf.wgt;
        etf.holdings.forEach(stock => {
            const { symbol, wgt } = stock;
            if (!consolidatedStocks[symbol]) {
                consolidatedStocks[symbol] = {
                    wgt: 0, parents: [], contributions: {},
                    sector: stock?.sector, industry: stock?.industry
                };
            }
            const stockWeight = wgt * etfWeight;
            consolidatedStocks[symbol].wgt += stockWeight;
            consolidatedStocks[symbol].parents.push(etfSymbol);

            if (!consolidatedStocks[symbol].contributions[etfSymbol]) {
                consolidatedStocks[symbol].contributions[etfSymbol] = { absolute: 0, relative: 0 };
            }
            const absoluteContribution = wgt / etfContributions[etfSymbol];
            consolidatedStocks[symbol].contributions[etfSymbol].absolute = absoluteContribution;
            consolidatedStocks[symbol].contributions[etfSymbol].relative = stockWeight;
        });
    }

    // Sort stocks by consolidated weight
    const sortedStocks = Object.entries(consolidatedStocks).sort((a, b) => b[1].wgt - a[1].wgt);

    // Headers for detailed rows
    const headers = ['Ticker', 'Weight', 'Rolling Total'];
    Object.keys(etfs).forEach(etfSymbol => {
        headers.push(`${etfSymbol} Abs`, `${etfSymbol} Rel`);
    });
    const detailedRows = [headers];
    // Calculate cumulative weights and prepare output formats
    sortedStocks.forEach(([symbol, data], index) => {
        const row = [symbol, data.wgt.toFixed(4), '']; // Placeholder for cumulative weight

        // Calculate and add ETF-specific cumulative weights
        Object.keys(etfs).forEach(etfSymbol => {
            const contributions = data.contributions[etfSymbol] || { absolute: 0, relative: 0 };
            etfCumulativeWeights[etfSymbol].absolute += contributions.absolute;
            etfCumulativeWeights[etfSymbol].relative += contributions.relative / etfWeights[etfSymbol];

            row.push(etfCumulativeWeights[etfSymbol].absolute.toFixed(4), etfCumulativeWeights[etfSymbol].relative.toFixed(4));
        });

        detailedRows.push(row);
    });

    // Adjust cumulative weight calculation
    let cumulativeWeight = 0;
    detailedRows.slice(1).forEach(row => {
        cumulativeWeight += parseFloat(row[1]);
        row[2] = cumulativeWeight.toFixed(4); // Update cumulative weight in the row
    });

    // Simplified objects remain unchanged
    let cumsum = 0;
    const simplifiedObjects = sortedStocks.map(([symbol, data], index) => {
        cumsum += data.wgt;
        return {
            symbol: symbol,
            wgt: data.wgt,
            cumsum: cumsum,
            index: index + 1,
            skew: data.wgt - sortedStocks[0][1].wgt,
            parents: data.parents,
            sector: data.sector
        }
    });

    return { detailedRows, simplifiedObjects };
}



function getSubset(objectList, cutoff) {
    const clonedObjectList = [...objectList];
    clonedObjectList.sort((a, b) => b.wgt - a.wgt);
    const subset = [];
    const outset = [];
    // Assuming detailedRows[0] contains headers and actual data starts from detailedRows[1]
    for (let i = 0; i < clonedObjectList.length; i++) {
        const row = clonedObjectList[i];
        if (row.cumsum <= cutoff) {
            subset.push(row); // Assuming the ticker symbol is in the first position
        } else {
            outset.push(row);
        }
    }

    return { subset, outset };
}





let _listToMapping = (list)=>{
    let mapping = {};
    list.forEach(item=>{
        mapping[item.symbol] = item;
    });
    return mapping;
}



class SectorReallocationScheme {

    constructor(subset, otherSet, sectorThreshold, nNextTickers) {
        this.subset = deepCopy(subset); // Ensure deepCopy function is implemented to handle deep cloning
        this.otherSet = deepCopy(otherSet);
        this.sectorThreshold = sectorThreshold;
        this.mapping = _listToMapping([...this.subset, ...this.otherSet]);

    }

    _getByTag(tickerSet, matchTag){
        return tickerSet.reduce((acc, item) => {
            let tag = item?.[matchTag] ?? 'missing';
            if (!acc[tag]) {
                acc[tag] = {
                    count: 0,
                    tickers: [],
                    wgt: 0
                };
            }
            acc[tag].count += 1;
            acc[tag].tickers.push(item.symbol);
            acc[tag].wgt += item.wgt;
            return acc;
        }, {});
    }

    /**
     * Get the sector adjusted portfolio list (cutoff subset + needed otherSet tickers to meet sector threshold)
     * @returns {*[]}
     */
    getAdjustedPortfolioList() {
    // Object to track the adjusted list of tickers for each sector
        const nextOutWeights = {};
        let addedTickers = [];
        let portfolio = [...deepCopy(this.subset)];

        // Aggregate sector weights in otherSet
        const otherSetSectorWeights = this._getByTag(this.otherSet, 'sector');
        // Iterate through each sector in the otherSet
        for (let sector in otherSetSectorWeights) {
            const sectorData = otherSetSectorWeights[sector];
            const sectorWeight = sectorData.wgt;
            const tickers = sectorData.tickers;
            /**
             * If the sector weight - sectorThreshold is greater than 0, we will continue with
             * ... the reallocation process. Otherwise, we will skip the sector because its in line within
             * ... the sectorThreshold tolerance
             */
            if (tickers.length > 0 && sectorWeight - this.sectorThreshold > 0) {
                let adjustedSectorWeight = sectorWeight - this.sectorThreshold;
                /**
                 * Finding the weight of the first ticker in the out set is critical here for correctly ensuring
                 * the weights of tickers added back in remain in line with the smallest weights currently within
                 * subset.
                 */
                const firstTickerWeight = this.mapping[tickers[0]].wgt;
                let hasRemainder = false;
                // Determine number of tickers needed to match the sector weight
                let tickersNeeded = Math.round(adjustedSectorWeight / firstTickerWeight);
                let remainderWeight = tickersNeeded * firstTickerWeight - adjustedSectorWeight;

                if (remainderWeight > 0 && tickersNeeded < tickers.length) {
                    /**
                     * If there is a remainder weight and the number of tickers needed is less than the total
                     * ... number of tickers in the sector, we will add 1 to the number of tickers needed
                     */
                    tickersNeeded++;
                    hasRemainder = true;
                }
                const selectedTickers = tickers.slice(0, tickersNeeded);  // Select tickers to include
                nextOutWeights[sector] = {
                    tickers: selectedTickers,
                    equalWeight: firstTickerWeight,   // Equal weight of each ticker
                    remainderWeight: remainderWeight,   // Remainder weight of the sector for +1 ticker
                    remainderTicker: hasRemainder === true ? selectedTickers[selectedTickers.length - 1] : null
                };
                addedTickers.push(...selectedTickers.map(x=>x));
            }
        }

        // Build the final portfolio
        for (let sector in nextOutWeights) {
            let [tickers, equalWeight, remainderWeight, remainderTicker] = [
                nextOutWeights[sector].tickers,
                nextOutWeights[sector].equalWeight,
                nextOutWeights[sector].remainderWeight,
                nextOutWeights[sector].remainderTicker
            ];
            tickers.forEach(ticker => {
                let obj = this.mapping[ticker];
                obj.added = addedTickers.includes(ticker);
                obj.isRemainder = false;
                if (obj.added===true){
                    if (ticker != remainderTicker) {
                        obj.wgt = equalWeight;
                    }
                    else {
                        obj.wgt = remainderWeight;
                        obj.isRemainder = true;
                    }
                }
                portfolio.push(obj);
            });
        }
        return portfolio;
    }

    reallocateSectorsToSubset(adjustedPortfolioList, remainderSectorWeights) {
        // Calculate the total weight for each sector in the adjusted portfolio
        let copiedAdjustedPortfolioList = deepCopy(adjustedPortfolioList);

        for (let sector in remainderSectorWeights){
            let remainderWeight = remainderSectorWeights[sector];
            if (remainderWeight>0){
                let tickersInSector = copiedAdjustedPortfolioList.filter(x=>x?.sector===sector);
                let wgtPerTicker = remainderWeight / tickersInSector.length;
                tickersInSector.forEach(x=>x.wgt+=wgtPerTicker);
            }
        }
        let totalWeight = copiedAdjustedPortfolioList.reduce((acc, x)=>acc+x.wgt, 0);
        if (totalWeight>1){

        }
        return copiedAdjustedPortfolioList;
    }
    /**
    * Get the sector weights for everything not in adjustedPortfolioList
    * @param adjustedPortfolioList
    * @returns {{}}
    */
    getRemainderSectorWeights(adjustedPortfolioList) {
        let totalAdjustedWeight = adjustedPortfolioList.reduce((acc, item) => acc + item.wgt, 0);
        let sectorWeights = {};
        let uniqueSymbols = adjustedPortfolioList.map(x => x.symbol);
        this.otherSet.forEach(item => {
            if (!uniqueSymbols.includes(item.symbol)) {
                let sector = item?.sector ?? 'missing';
                if (!sectorWeights[sector]) {
                    sectorWeights[sector] = 0;
                }
                sectorWeights[sector] += item.wgt;
            }
        });

        // Normalize the sector weights based on the total adjusted weight
        for (let sector in sectorWeights) {
            sectorWeights[sector] = sectorWeights[sector] / totalAdjustedWeight;
        }

        return sectorWeights;
    }
}






export const sectorTargetFillPortfolio = (subset, outset, sectorTolerance = 0.02, nNextTickers = 2) => {
    let sectorScheme = new SectorReallocationScheme(subset, outset, sectorTolerance, nNextTickers);
    let adjustedPortfolioList = sectorScheme.getAdjustedPortfolioList();
    let remainderSectorWeights = sectorScheme.getRemainderSectorWeights(adjustedPortfolioList);
    let reallocatedPortfolioList = sectorScheme.reallocateSectorsToSubset(adjustedPortfolioList, remainderSectorWeights);
    return proRataFillPortfolio(reallocatedPortfolioList);
}




export { getConsolidatedStockList, getSubset}